import pluralize from "pluralize";
import {
  ComplexEntityTypes,
  ControlStatus,
  JiraStatus,
  PING_STATUS,
  RemediationPriority,
  RemediationStatus,
} from "../../../../../utils/constants";
import { getIsAdminList } from "../../../../../utils/search";
import { getEntityTypeAndUniqueIdFromEntityId } from "../../../../../utils/utils";

export function capitalizeFirstLetter(string) {
  return string.replace(/\b\w/g, function (char) {
    return char.toUpperCase();
  });
}

export function capitalizeAndRemoveUnderscores(string) {
  return string
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

const special_types = {
  iam: "IAM",
  aws: "AWS",
  kms: "KMS",
  bucket: "S3 Bucket",
  "nonhuman identity": "NHI",
  edr: "EDR",
};

export function getEntitiesLabel(entitiesCount, entityType) {
  // Insert a space before any uppercase letter that is not at the start of entityType.
  entityType = entityType.replace(/([a-z])([A-Z])/g, "$1 $2");

  // Replaces all occurrences of underscore "_" in entityType with a space.
  entityType = entityType.replace(/_/g, " ");

  // replacing any occurrence of special type in entityType with their corresponding values.
  for (const [key, value] of Object.entries(special_types)) {
    if (entityType.toLowerCase().includes(key)) {
      entityType = entityType.replace(key, value);
    }
  }

  if (entitiesCount === 1) {
    return `1 ${capitalizeFirstLetter(entityType)}`;
  }

  // remove space at the end of the string if it exists.
  entityType = entityType.replace(/\s+$/, "");

  // Handle the special case for "NHI"
  if (entityType === "NHI") {
    return `${entitiesCount} NHIs`;
  }

  return `${entitiesCount} ${capitalizeFirstLetter(pluralize(entityType))}`;
}

export function getEntitiesTooltip(entitiesCount, entityType, totalCount) {
  if (!totalCount) return "";

  // Insert a space before any uppercase letter that is not at the start of entityType.
  entityType = entityType.replace(/([a-z])([A-Z])/g, "$1 $2");

  // Replaces all occurrences of underscore "_" in entityType with a space.
  entityType = entityType.replace(/_/g, " ");

  // replacing any occurrence of special type in entityType with their corresponding values.
  for (const [key, value] of Object.entries(special_types)) {
    if (entityType.toLowerCase().includes(key)) {
      entityType = entityType.replace(key, value);
    }
  }

  if (entitiesCount === 1) {
    return `1 out of ${totalCount} ${capitalizeFirstLetter(entityType)}`;
  }

  // remove space at the end of the string if it exists.
  entityType = entityType.replace(/\s+$/, "");

  if (
    entityType === "NHI" ||
    entityType === "IAM User" ||
    entityType === "KMS"
  ) {
    return `${entitiesCount} out of ${totalCount} NHIs`;
  }

  return `${entitiesCount} out of ${totalCount} ${capitalizeFirstLetter(
    pluralize(entityType, totalCount)
  )}`;
}

export function extractTypeFromEntityId(entityId) {
  const [entityType] = getEntityTypeAndUniqueIdFromEntityId(entityId);
  return entityType;
}

export function extractUniqueIdFromEntityId(entityId) {
  const [, uniqueId] = getEntityTypeAndUniqueIdFromEntityId(entityId);
  return uniqueId;
}

export const capitalizeAndPluralize = (str) => {
  return pluralize(str.charAt(0).toUpperCase() + str.slice(1));
};

export const getStatusColor = (status, colors) => {
  const colorLabel = {
    soft: {
      error: {
        light: {
          background: colors.error_soft_label_fill,
          color: colors.error_light_label_text,
        },
      },
      success: {
        light: {
          background: colors.success_soft_label_fill,
          color: colors.success_soft_label_text,
        },
      },
      secondary: {
        light: {
          background: colors.secondary_soft_label_fill,
          color: colors.secondary_dark,
        },
      },
    },
  };

  const statusColors = {
    [ControlStatus.EXCLUDED]: colors.grey_dark,
    [ControlStatus.SNOOZED]: colorLabel.soft.secondary.light.color,
    [ControlStatus.GAP]: colorLabel.soft.error.light.color,
    [ControlStatus.COMPLIANT]: colorLabel.soft.success.light.color,
    [RemediationStatus.PENDING]: colors.grey_500,
    [RemediationStatus.PARTIALLY_RESOLVED]: colors.success_dark,
    [RemediationStatus.RESOLVED]: colors.success_dark,
    [PING_STATUS.APPROVED]: colors.success_dark,
    [PING_STATUS.PING_SENT]: colors.info_main,
  };

  return statusColors[status] || colors.grey_dark;
};

export const getPriorityColor = (priority, colors) => {
  const backgroundAndBorderColor = {
    [RemediationPriority[1]]: {
      background: colors.grey_20percent,
      border: colors.grey_500,
    },
    [RemediationPriority[2]]: {
      background: colors.primary_8percent,
      border: colors.primary_light,
    },
    [RemediationPriority[3]]: {
      background: colors.primary_16percent,
      border: colors.primary_main,
    },
    [RemediationPriority[4]]: {
      background: colors.primary_16percent,
      border: colors.primary_dark,
    },
    [RemediationPriority[5]]: {
      background: colors.primary_24percent,
      border: colors.primary_darker,
    },
  };

  return backgroundAndBorderColor[priority] || backgroundAndBorderColor.lowest;
};

export const getFeasibilityColor = (feasibility, colors) => {
  const backgroundAndBorderColor = {
    "Very Low": {
      background: colors.feasibility_very_low_fill,
      border: colors.feasibility_very_low_stroke,
    },
    Low: {
      background: colors.feasibility_low_fill,
      border: colors.feasibility_low_stroke,
    },
    Medium: {
      background: colors.feasibility_medium_fill,
      border: colors.feasibility_medium_stroke,
    },
    High: {
      background: colors.feasibility_high_fill,
      border: colors.feasibility_high_stroke,
    },
    "Very High": {
      background: colors.feasibility_very_high_fill,
      border: colors.feasibility_very_high_stroke,
    },
  };

  return backgroundAndBorderColor[feasibility] || backgroundAndBorderColor.low;
};

export const getJiraStatusColor = (status, colors) => {
  const jiraColor = {
    [JiraStatus.TODO]: colors.grey_dark,
    [JiraStatus.IN_PROGRESS]: colors.jira_in_progress,
    [JiraStatus.DONE]: colors.jira_done,
  };

  return jiraColor[status] || colors.jira_to_do;
};

export const groupEntityIdsByType = (entityIds) => {
  return entityIds.reduce((acc, entityId) => {
    const type = extractTypeFromEntityId(entityId);
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(entityId);
    return acc;
  }, {});
};

export const isHibobControlWithSpecificIds = (type, control) => {
  return (
    type === ComplexEntityTypes.USER &&
    control?.tool === "hibob" &&
    [284, 285, 286, 287].includes(control?.control_id)
  );
};

export const getEntityActiveInHiBobControls = (control, entity) => {
  if (!entity) return [];

  const filterOutHibob = (tool) => tool.toLowerCase() !== "hibob";

  if ([284, 286].includes(control?.control_id)) {
    // For admin controls, return only the admin list
    return getIsAdminList(entity).filter(filterOutHibob);
  }

  const sources = Array.isArray(entity.sources) ? entity.sources : [];
  return sources.filter(filterOutHibob);
};

export const getIncludedEntityIds = (fullControl) => {
  const includedMisconfigs = fullControl?.misconfigurations?.filter(
    (misconfiguration) => {
      return !misconfiguration.is_excluded;
    }
  );

  const affectedEntities = fullControl?.misconfigurations?.flatMap(
    (misconfiguration) => {
      return misconfiguration.affected_entities;
    }
  );
  const includedAffectedEntities = affectedEntities?.filter(
    (affectedEntity) => {
      return !affectedEntity.is_excluded;
    }
  );

  const allIncludedMisconfigs = [
    ...includedMisconfigs,
    ...includedAffectedEntities,
  ];
  return allIncludedMisconfigs.map((misconfiguration) => {
    return misconfiguration.entity_id;
  });
};

export const getControlEntityIds = (fullControl) => {
  const misconfiguredEntityIds = fullControl?.misconfigurations?.map(
    (misconfiguration) => {
      return misconfiguration.entity_id;
    }
  );
  const affectedEntityIds = fullControl?.misconfigurations?.flatMap(
    (misconfiguration) => {
      return misconfiguration.affected_entity_ids;
    }
  );

  return [...misconfiguredEntityIds, ...affectedEntityIds];
};
